import React, { useState } from 'react';
import { useCooking } from '../hooks/useCooking';
import { useDraggablePanel } from '../utils/panelDragUtils';
import '../styles/Cooking.css';

interface CookingPanelProps {
  socketRef: React.MutableRefObject<any>;
  isOpen: boolean;
  onClose: () => void;
}

export const CookingPanel: React.FC<CookingPanelProps> = ({ socketRef, isOpen, onClose }) => {
  const { cookingState, recipes, startCooking, stopCooking, isLoading, error } =
    useCooking(socketRef);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(200, 150);

  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen) return null;

  const selectedRecipe = recipes.find(r => r.id === selectedRecipeId);
  const xpPercent = cookingState.nextLevelXp > 0
    ? ((cookingState.xp / cookingState.nextLevelXp) * 100)
    : 0;

  return (
    <div className="cooking-panel" ref={panelRef} style={dragStyle}>
      <div className="cooking-panel__header" onMouseDown={handleHeaderMouseDown}>
        <h2>Cooking</h2>
        <button className="cooking-panel__close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Skill Stats */}
      <div className="cooking-panel__stats">
        <div className="cooking-stats">
          <div className="cooking-stat">
            <span className="cooking-stat__label">Level</span>
            <span className="cooking-stat__value">{cookingState.level}</span>
          </div>
          <div className="cooking-stat">
            <span className="cooking-stat__label">XP</span>
            <span className="cooking-stat__value">{cookingState.xp.toLocaleString()}</span>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="cooking-progress">
          <div className="cooking-progress__bar">
            <div className="cooking-progress__fill" style={{ width: `${xpPercent}%` }} />
          </div>
          <div className="cooking-progress__text">
            {cookingState.xp.toLocaleString()} / {cookingState.nextLevelXp.toLocaleString()} XP
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="cooking-error">{error}</div>}

      {/* Current Cooking Status */}
      {cookingState.isCooking && cookingState.currentRecipe && (
        <div className="cooking-status">
          <div className="cooking-status__recipe">{cookingState.currentRecipe.name}</div>
          <div className="cooking-status__progress">
            {cookingState.itemsCooked} / {cookingState.batchSize} items
          </div>
          <button className="cooking-stop-btn" onClick={stopCooking}>
            Stop Cooking
          </button>
        </div>
      )}

      {/* Recipe Selection */}
      {!cookingState.isCooking && (
        <div className="cooking-recipes">
          <h3>Available Recipes</h3>
          {isLoading ? (
            <div className="cooking-loading">Loading recipes...</div>
          ) : recipes.length === 0 ? (
            <div className="cooking-no-recipes">No recipes available at your level</div>
          ) : (
            <div className="cooking-recipe-list">
              {recipes.map(recipe => (
                <div
                  key={recipe.id}
                  className={`cooking-recipe ${selectedRecipeId === recipe.id ? 'active' : ''}`}
                  onClick={() => setSelectedRecipeId(recipe.id)}
                >
                  <div className="cooking-recipe__name">{recipe.name}</div>
                  <div className="cooking-recipe__meta">
                    <span className="cooking-recipe__level">Lvl {recipe.requiredLevel}</span>
                    <span className="cooking-recipe__xp">{recipe.baseXp} XP</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recipe Details & Start Cooking */}
          {selectedRecipe && (
            <div className="cooking-recipe-details">
              <h4>{selectedRecipe.name}</h4>
              <p className="cooking-recipe-description">{selectedRecipe.description}</p>

              <div className="cooking-recipe-info">
                <div className="cooking-info-row">
                  <span>Required Level:</span>
                  <strong>{selectedRecipe.requiredLevel}</strong>
                </div>
                <div className="cooking-info-row">
                  <span>Cook Time:</span>
                  <strong>{(selectedRecipe.baseCookTime / 1000).toFixed(1)}s</strong>
                </div>
                <div className="cooking-info-row">
                  <span>XP per Item:</span>
                  <strong>{selectedRecipe.baseXp}</strong>
                </div>
                {selectedRecipe.canBurn && (
                  <div className="cooking-info-row">
                    <span>Max Burn Rate:</span>
                    <strong>{(selectedRecipe.baseBurnRate * 100).toFixed(0)}%</strong>
                  </div>
                )}
              </div>

              {/* Quantity Input */}
              <div className="cooking-quantity">
                <label htmlFor="cooking-quantity">Quantity:</label>
                <input
                  id="cooking-quantity"
                  type="number"
                  min="1"
                  max="99"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>

              {/* Start Cooking Button */}
              <button
                className="cooking-start-btn"
                onClick={() => {
                  startCooking(selectedRecipe.id, quantity);
                  setSelectedRecipeId(null);
                }}
                disabled={cookingState.level < selectedRecipe.requiredLevel}
              >
                Start Cooking ({quantity}x)
              </button>

              {cookingState.level < selectedRecipe.requiredLevel && (
                <div className="cooking-level-requirement">
                  Requires Level {selectedRecipe.requiredLevel}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
